import React, {useMemo, useState} from 'react';
import {ActionIcon, Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconChevronLeft, IconChevronRight} from '@tabler/icons-react';
import type {CourseClassroomSimpleResponse} from 'src/api/ProactApiClient';
import {Spinner} from 'src/design/atoms/Spinner';
import {useGetCourseClassroomListV2Query} from 'src/redux/queries/courseClassroom';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {ScheduleFilters} from './ScheduleFiltersBar';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ScheduleCalendarViewProps {
  filters: ScheduleFilters;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const startOfCalendarGrid = (date: Date) => {
  const first = startOfMonth(date);
  const day = first.getDay();
  return new Date(first.getFullYear(), first.getMonth(), first.getDate() - day);
};

/**
 * Simple month-grid calendar view of the Schedule page, built with Mantine
 * primitives (no calendar component existed anywhere in the app to reuse).
 */
export const ScheduleCalendarView: React.FC<ScheduleCalendarViewProps> = ({filters}) => {
  const navigate = useAppNavigate();
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));

  const monthStart = startOfMonth(monthAnchor);
  const gridStart = startOfCalendarGrid(monthAnchor);
  const days = useMemo(
    () =>
      Array.from({length: 42}, (_, index) => {
        const day = new Date(gridStart);
        day.setDate(gridStart.getDate() + index);
        return day;
      }),
    [gridStart]
  );

  const {data, isLoading} = useGetCourseClassroomListV2Query({
    start: 0,
    length: 500,
    filters: [],
    filterFns: [],
    sorting: [{id: 'startsAt', desc: false}],
    courseDefinitionId: filters.courseDefinitionId
      ? Number(filters.courseDefinitionId)
      : undefined,
    instructorId: filters.instructorId ? Number(filters.instructorId) : undefined,
    states: filters.states.length ? filters.states : undefined,
    startsAfter: gridStart.toISOString(),
    startsBefore: new Date(gridStart.getTime() + 42 * 24 * 60 * 60 * 1000).toISOString(),
  });

  const classroomsByDay = useMemo(() => {
    const map = new Map<string, CourseClassroomSimpleResponse[]>();
    (data?.data ?? []).forEach(classroom => {
      if (!classroom.startsAt) return;
      const key = parseUtcDate(classroom.startsAt).toDateString();
      const existing = map.get(key) ?? [];
      existing.push(classroom);
      map.set(key, existing);
    });
    return map;
  }, [data]);

  const handleOpen = (classroom: CourseClassroomSimpleResponse) =>
    navigate(
      PathUtility.TrainingCourseClassroomView(
          String(classroom.courseDefinitionId),
          String(classroom.id),
          'roster'
        )
    );

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={700} size="lg">
          {monthStart.toLocaleString(undefined, {month: 'long', year: 'numeric'})}
        </Text>
        <Group gap="xs">
          <ActionIcon
            variant="subtle"
            aria-label="Previous month"
            onClick={() =>
              setMonthAnchor(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1))
            }>
            <IconChevronLeft size={18} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            aria-label="Next month"
            onClick={() =>
              setMonthAnchor(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1))
            }>
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
      </Group>

      {isLoading ? (
        <Spinner loadingText="Loading schedule...." />
      ) : (
        <>
          <SimpleGrid cols={7} spacing={2}>
            {WEEKDAY_LABELS.map(label => (
              <Text key={label} size="xs" fw={600} c="dimmed" ta="center">
                {label}
              </Text>
            ))}
          </SimpleGrid>
          <SimpleGrid cols={7} spacing={2}>
            {days.map(day => {
              const inMonth = day.getMonth() === monthStart.getMonth();
              const classroomsForDay = classroomsByDay.get(day.toDateString()) ?? [];
              return (
                <Box
                  key={day.toISOString()}
                  p="xs"
                  style={{
                    minHeight: 96,
                    border: '1px solid var(--mantine-color-gray-2)',
                    opacity: inMonth ? 1 : 0.4,
                  }}>
                  <Text size="xs" c="dimmed">
                    {day.getDate()}
                  </Text>
                  <Stack gap={2} mt={4}>
                    {classroomsForDay.slice(0, 3).map(classroom => (
                      <Badge
                        key={classroom.id}
                        variant="light"
                        color="blue"
                        style={{cursor: 'pointer', textOverflow: 'ellipsis'}}
                        onClick={() => handleOpen(classroom)}
                        fullWidth>
                        {classroom.courseDefinition?.name ?? 'Class'}
                      </Badge>
                    ))}
                    {classroomsForDay.length > 3 && (
                      <Text size="xs" c="dimmed">
                        +{classroomsForDay.length - 3} more
                      </Text>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
};
